import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

import { Button, Textarea } from "@/components/ui";
import { useAriClientNotes } from "@/hooks/portfolio/useAriClientNotes";

export function ClientNotesPanel({ clientId }: { clientId: string }) {
  const { notes, saveNotes, isLoading } = useAriClientNotes(clientId);
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(null);
  }, [clientId, notes]);

  const value = draft ?? notes;

  const handleSave = async () => {
    setIsSaving(true);
    await saveNotes(value);
    setDraft(null);
    setIsSaving(false);
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading notes…</p>;
  }

  return (
    <div className="max-w-2xl space-y-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        <Sparkles className="size-4 text-primary" aria-hidden="true" />
        Notes
      </div>
      <Textarea
        value={value}
        onChange={(event) => setDraft(event.target.value)}
        rows={12}
        placeholder="Add context, follow-ups, or Ari insights for this client…"
        className="min-h-[220px] resize-y text-sm leading-6"
        aria-label="Client notes"
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={draft === null || draft === notes || isSaving}
          onClick={() => setDraft(null)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={draft === null || draft === notes || isSaving}
          onClick={() => void handleSave()}
        >
          {isSaving ? "Saving…" : "Save notes"}
        </Button>
      </div>
    </div>
  );
}
